import React from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RootStackParamList } from '../types/navigation';
import { Icon } from '../components/Icon';
import { colors, spacing, fontWeights, borderRadius } from '../theme';

type NavigationProp = StackNavigationProp<RootStackParamList, 'M12ProfileScreen'>;

export const M12ProfileScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();

  const handleLogout = () => {
    // Navigate back to Login
    navigation.replace('M02LoginScreen');
  };

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        {/* Profile Card */}
        <View style={styles.profileCard}>
          <View style={styles.avatar}>
            <Text style={styles.avatarText}>M</Text>
          </View>
          <View style={styles.info}>
            <Text style={styles.name}>Nguyễn Hồng Minh</Text>
            <Text style={styles.role}>Đại lý Chuyên nghiệp • Manulife VN</Text>
            <Text style={styles.code}>Mã đại lý: ML-982341</Text>
          </View>
        </View>

        {/* Gói thuê bao */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>💼 Gói dịch vụ của bạn</Text>
          <View style={styles.packageDetails}>
            <View style={styles.packageRow}>
              <Text style={styles.packageLabel}>Gói hiện tại:</Text>
              <View style={styles.packageBadge}>
                <Text style={styles.packageBadgeText}>PRO PLAN</Text>
              </View>
            </View>
            <View style={styles.packageRow}>
              <Text style={styles.packageLabel}>Hạn dùng đến:</Text>
              <Text style={styles.packageValue}>18/06/2027 (Còn 364 ngày)</Text>
            </View>
            <View style={styles.packageRow}>
              <Text style={styles.packageLabel}>Mức phí:</Text>
              <Text style={styles.packageValue}>1.000.000 VNĐ / năm</Text>
            </View>
          </View>
          <TouchableOpacity style={styles.renewBtn}>
            <Text style={styles.renewBtnText}>Gia hạn thuê bao</Text>
          </TouchableOpacity>
        </View>

        {/* Thiết lập đại lý (Branding) */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>⚙️ Cấu hình Branding Proposal</Text>
          
          <TouchableOpacity style={styles.settingItem}>
            <View style={styles.settingLeft}>
              <Icon name="face" size={20} color={colors.onSurfaceVariant} />
              <Text style={styles.settingLabel}>Cập nhật ảnh đại diện</Text>
            </View>
            <Icon name="chevron_right" size={20} color={colors.onSurfaceVariant} />
          </TouchableOpacity>

          <TouchableOpacity style={styles.settingItem}>
            <View style={styles.settingLeft}>
              <Icon name="calculate" size={20} color={colors.onSurfaceVariant} />
              <Text style={styles.settingLabel}>Tải lên Logo đại lý cá nhân</Text>
            </View>
            <Icon name="chevron_right" size={20} color={colors.onSurfaceVariant} />
          </TouchableOpacity>

          <TouchableOpacity style={styles.settingItem}>
            <View style={styles.settingLeft}>
              <Icon name="phone" size={20} color={colors.onSurfaceVariant} />
              <Text style={styles.settingLabel}>Số điện thoại liên hệ trên PDF</Text>
            </View>
            <Icon name="chevron_right" size={20} color={colors.onSurfaceVariant} />
          </TouchableOpacity>
        </View>

        {/* Logout */}
        <TouchableOpacity style={styles.logoutBtn} onPress={handleLogout}>
          <Icon name="logout" size={20} color="#ff3b30" />
          <Text style={styles.logoutText}>Đăng xuất tài khoản</Text>
        </TouchableOpacity>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f7fa',
  },
  scrollContent: {
    padding: spacing[4],
    gap: spacing[4],
    paddingBottom: 88,
  },
  profileCard: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.lg,
    padding: spacing[5],
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[4],
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 6,
    elevation: 1,
  },
  avatar: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: colors.primaryFixed,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: colors.primary,
  },
  avatarText: {
    fontSize: 22,
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  info: {
    gap: spacing[1],
  },
  name: {
    fontSize: 16,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  role: {
    fontSize: 12.5,
    color: colors.onSurfaceVariant,
  },
  code: {
    fontSize: 11,
    color: colors.onSurfaceVariant,
    fontStyle: 'italic',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    borderWidth: 1,
    borderColor: colors.outlineVariant,
  },
  cardTitle: {
    fontSize: 14.5,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
    marginBottom: spacing[3],
  },
  packageDetails: {
    gap: spacing[2.5],
    marginBottom: spacing[4],
  },
  packageRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  packageLabel: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
  },
  packageValue: {
    fontSize: 13,
    fontWeight: fontWeights.semibold,
    color: colors.onSurface,
  },
  packageBadge: {
    backgroundColor: 'rgba(0,166,81,0.1)',
    paddingHorizontal: spacing[2],
    paddingVertical: spacing[0.5],
    borderRadius: borderRadius.sm,
  },
  packageBadgeText: {
    fontSize: 11,
    fontWeight: fontWeights.bold,
    color: colors.secondary,
  },
  renewBtn: {
    backgroundColor: 'rgba(0,51,102,0.06)',
    borderRadius: borderRadius.DEFAULT,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  renewBtnText: {
    fontSize: 13.5,
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  settingItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing[3],
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.outlineVariant,
  },
  settingLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[3],
  },
  settingLabel: {
    fontSize: 13.5,
    color: colors.onSurface,
  },
  logoutBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    height: 48,
    gap: spacing[2],
    borderWidth: 1,
    borderColor: '#ff3b30',
  },
  logoutText: {
    fontSize: 14.5,
    fontWeight: fontWeights.bold,
    color: '#ff3b30',
  },
});
